import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { FiInfo, FiUserPlus, FiX } from "react-icons/fi";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import {
  API_EMPLOYEE_TECH_STACK,
  API_PROJECT_ENROLLMENT_REQUESTS,
} from "../../config/Endpoints";
import { getApi, postApi } from "../../utils/api";
import styles from "./ProjectEnrollmentRequestModal.module.css";

const MAX_WORDS = 200;

const countWords = (value) => {
  const normalized = value.trim();
  return normalized ? normalized.split(/\s+/).length : 0;
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error?.response?.data === "string" ? error.response.data : fallback);

export default function ProjectEnrollmentRequestModal({ open, onClose }) {
  const navigate = useNavigate();
  const accessToken = Cookies.get("accessToken");
  const [skills, setSkills] = useState([]);
  const [latestRequest, setLatestRequest] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");

  const wordCount = useMemo(() => countWords(description), [description]);
  const hasSkills = skills.length > 0;
  const hasPendingRequest = latestRequest?.status === "PENDING";
  const canSubmit =
    hasSkills &&
    !hasPendingRequest &&
    wordCount > 0 &&
    wordCount <= MAX_WORDS &&
    !submitting;

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      setDescription("");
      try {
        const [skillsResponse, requestResponse] = await Promise.all([
          getApi(API_EMPLOYEE_TECH_STACK, accessToken),
          getApi(`${API_PROJECT_ENROLLMENT_REQUESTS}/mine/latest`, accessToken),
        ]);
        if (!active) return;
        setSkills(skillsResponse.data?.data?.techStacks || skillsResponse.data?.techStacks || []);
        setLatestRequest(requestResponse.data?.data || requestResponse.data || null);
      } catch (error) {
        if (!active) return;
        setSkills([]);
        setLatestRequest(null);
        setLoadError(errorMessage(error, "Unable to load your tech skills."));
      } finally {
        if (active) setLoading(false);
      }
    };

    document.body.style.overflow = "hidden";
    void load();

    return () => {
      active = false;
      document.body.style.overflow = "";
    };
  }, [accessToken, open]);

  if (!open) return null;

  const close = () => {
    if (!submitting) onClose();
  };

  const goToProfile = () => {
    onClose();
    navigate("/dashboard/myProfile");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      const response = await postApi(
        API_PROJECT_ENROLLMENT_REQUESTS,
        { description: description.trim() },
        accessToken,
      );
      setLatestRequest(response.data?.data || response.data);
      toast.success("Your request to join a project has been submitted.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to submit your request."), {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.backdrop} role="presentation" onMouseDown={close}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-enrollment-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.titleIcon}><FiUserPlus aria-hidden /></div>
          <div className={styles.heading}>
            <h2 id="project-enrollment-title">Request to Join a Project</h2>
            <p>Your request will be reviewed based on your skills and availability.</p>
          </div>
          <button className={styles.closeButton} type="button" onClick={close} aria-label="Close">
            <FiX aria-hidden />
          </button>
        </header>

        <form onSubmit={submit}>
          <div className={styles.content}>
            {loading ? (
              <div className={styles.loading}>
                <ThreeDots height="38" width="52" radius="9" color="#3478f6" visible />
                <span>Loading your tech skills...</span>
              </div>
            ) : (
              <>
                <div className={`${styles.notice} ${!hasSkills ? styles.noticeWarning : ""}`}>
                  <FiInfo aria-hidden />
                  <div>
                    <strong>{hasSkills ? "Keep your tech skills updated" : "Please add your tech skills first"}</strong>
                    <p>
                      {hasSkills
                        ? "Your project placement will be considered based on these skills. Update your Profile section before submitting if anything is missing."
                        : "Add your tech skills in the Profile section before raising a request to join a project."}
                    </p>
                  </div>
                </div>

                {loadError ? <p className={styles.error}>{loadError}</p> : null}

                {hasPendingRequest ? (
                  <div className={styles.pendingNotice}>
                    You already have a pending request. A new request can be submitted after it is reviewed.
                  </div>
                ) : null}

                {hasSkills ? (
                  <div className={styles.skillsPanel}>
                    <h3>Your Tech Skills</h3>
                    <div className={styles.skills}>
                      {skills.map((skill) => (
                        <span className={styles.skill} key={skill.techStackId}>
                          {skill.techStack}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button className={styles.profileButton} type="button" onClick={goToProfile}>
                    Go to Profile
                  </button>
                )}

                {hasSkills && !hasPendingRequest ? (
                  <div className={styles.field}>
                    <label htmlFor="project-enrollment-description">
                      Request Description <span>*</span>
                    </label>
                    <textarea
                      id="project-enrollment-description"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      maxLength={2000}
                      rows={5}
                      placeholder="Describe why you would like to join a project (maximum 200 words)..."
                    />
                    <div className={wordCount > MAX_WORDS ? styles.counterError : styles.counter}>
                      {wordCount}/{MAX_WORDS} words
                    </div>
                    {wordCount > MAX_WORDS ? (
                      <p className={styles.error}>Description cannot exceed 200 words.</p>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>

          <footer className={styles.footer}>
            <button className={styles.cancelButton} type="button" onClick={close} disabled={submitting}>
              Cancel
            </button>
            {hasSkills && !hasPendingRequest ? (
              <button className={styles.submitButton} type="submit" disabled={!canSubmit || loading}>
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            ) : null}
          </footer>
        </form>
      </section>
    </div>
  );
}
